import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import logo from "../Images/para.png";

pdfMake.vfs = pdfFonts.vfs;

// ──────────────────────────────────────────────────────────────────────────
// HELPERS
// ──────────────────────────────────────────────────────────────────────────
const formatDateLong = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const months = [
    "JAN",
    "FEB",
    "MAR",
    "APR",
    "MAY",
    "JUN",
    "JUL",
    "AUG",
    "SEP",
    "OCT",
    "NOV",
    "DEC",
  ];
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = months[d.getMonth()];
  const yyyy = d.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

const formatCurrency = (amount) => {
  const val = Number(amount) || 0;
  return val.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const formatDateTime = (dateStr) => {
  const d = dateStr ? new Date(dateStr) : new Date();
  if (isNaN(d.getTime())) return dateStr;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
};

// ──────────────────────────────────────────────────────────────────────────
// SINGLE BILL PDF GENERATOR - BILL RECEIPT FORMAT
// ──────────────────────────────────────────────────────────────────────────
export const generateSingleBillPDF = (bill) => {
  const freights = bill.freights || [];

  // Extract first freight data for the slip
  const firstFreight = freights[0] || {};

  // Collect ALL packages across all freights
  const allPackages = [];
  freights.forEach((freight) => {
    const pkgs = freight.package_items || [];
    if (pkgs.length > 0) {
      pkgs.forEach((pkg) => {
        allPackages.push({ ...pkg, freight });
      });
    }
  });

  // Compute totals from ALL packages
  let totalFreightAmount = 0;
  let totalWeight = 0;
  allPackages.forEach((pkg) => {
    totalFreightAmount += parseFloat(pkg.amount) || 0;
    totalWeight += parseFloat(pkg.charged_weight) || 0;
  });

  // Fallback if no packages
  if (allPackages.length === 0) {
    totalFreightAmount = parseFloat(firstFreight.net_payable) || 0;
    totalWeight =
      parseFloat(firstFreight.loading_wt) ||
      parseFloat(firstFreight.received_wt) ||
      0;
  }

  const truckNo = firstFreight.truck_no || bill.truck_no || "-";
  const fromLocation = firstFreight.from_location || bill.from_location || "-";
  const toLocation = firstFreight.to_location || bill.to_location || "-";

  // Company / party info
  const companyContact = bill.company_contact || bill.contact_no || "-";
  const companyGstin = bill.company_gstin || bill.gst_no || "";
  const companyAddress = bill.address || "-";
  const memoNo = bill.billing_id;
  const billDate = formatDateLong(bill.created_at || bill.bill_date);

  // Account details
  const accountNumber = bill.account_number || "";
  const accountName = bill.account_name || "";
  const ifscCode = bill.ifsc_code || "";
  const bankName = bill.bank_name || "";
  const panCardNumber = "AAICV3639B";

  const getInitials = (s) =>
    s
      ?.split(" ")
      .filter(Boolean)
      .map((w) => w[0])
      .join("")
      .toUpperCase()
      .slice(0, 2) ?? "";

  // Remarks
  const remarks = bill.remarks || firstFreight.remark || "";

  // Owner company name for signature
  const ownerCompanyName = "PARAS ROADLINES";

  const HEADER_BG = "#e8e8e8"; // light grey for section headers

  // ═══════════════════════════════════════════════════════
  // BUILD BILL RECEIPT TABLE BODY
  // Columns: Srl | L.R.No. | Date | Vehicle No. | Transportation (From | To) | Materials | Wt. M.T. | Rate P.M.T. | Amount Rs.
  // ═══════════════════════════════════════════════════════
  const loadTableBody = [];

  // Header row 1 — Transportation spans 2 cols; others rowSpan 2
  loadTableBody.push([
    {
      text: "Srl",
      bold: true,
      fontSize: 8,
      alignment: "center",
      fillColor: HEADER_BG,
      rowSpan: 2,
      margin: [0, 6, 0, 0],
      noWrap: false,
    },
    {
      text: "L.R.\nNo.",
      bold: true,
      fontSize: 8,
      alignment: "center",
      fillColor: HEADER_BG,
      rowSpan: 2,
      margin: [0, 3, 0, 0],
      noWrap: false,
    },
    {
      text: "Date",
      bold: true,
      fontSize: 8,
      alignment: "center",
      fillColor: HEADER_BG,
      rowSpan: 2,
      margin: [0, 6, 0, 0],
      noWrap: false,
    },
    {
      text: "Vehicle\nNo.",
      bold: true,
      fontSize: 8,
      alignment: "center",
      fillColor: HEADER_BG,
      rowSpan: 2,
      margin: [0, 3, 0, 0],
      noWrap: false,
    },
    {
      text: "Transportation",
      bold: true,
      fontSize: 8,
      alignment: "center",
      fillColor: HEADER_BG,
      colSpan: 2,
      margin: [0, 2, 0, 2],
      noWrap: false,
    },
    {},
    {
      text: "Materials",
      bold: true,
      fontSize: 8,
      alignment: "center",
      fillColor: HEADER_BG,
      rowSpan: 2,
      margin: [0, 6, 0, 0],
      noWrap: false,
    },
    {
      text: "Wt.\nM.T.",
      bold: true,
      fontSize: 8,
      alignment: "center",
      fillColor: HEADER_BG,
      rowSpan: 2,
      margin: [0, 2, 0, 0],
      noWrap: false,
    },
    {
      text: "Rate\nM.T.",
      bold: true,
      fontSize: 8,
      alignment: "center",
      fillColor: HEADER_BG,
      rowSpan: 2,
      margin: [0, 2, 0, 0],
      noWrap: false,
    },
    {
      text: "Amount\nRs.",
      bold: true,
      fontSize: 8,
      alignment: "center",
      fillColor: HEADER_BG,
      rowSpan: 2,
      margin: [0, 2, 0, 0],
      noWrap: false,
    },
  ]);

  // Header row 2 — only From/To visible (others are spanned)
  loadTableBody.push([
    {},
    {},
    {},
    {},
    {
      text: "From",
      bold: true,
      fontSize: 8,
      alignment: "center",
      fillColor: HEADER_BG,
      margin: [0, 2, 0, 2],
      noWrap: false,
    },
    {
      text: "To",
      bold: true,
      fontSize: 8,
      alignment: "center",
      fillColor: HEADER_BG,
      margin: [0, 2, 0, 2],
      noWrap: false,
    },
    {},
    {},
    {},
    {},
  ]);

  // Data rows — one per package
if (allPackages.length > 0) {
  const freightGroups = [];
  let currentFreightId = null;
  let currentGroup = [];

  allPackages.forEach((pkg) => {
    const fId = pkg.freight?.freight_id ?? pkg.freight?.id ?? null;
    if (fId !== currentFreightId) {
      if (currentGroup.length > 0) freightGroups.push(currentGroup);
      currentGroup = [pkg];
      currentFreightId = fId;
    } else {
      currentGroup.push(pkg);
    }
  });
  if (currentGroup.length > 0) freightGroups.push(currentGroup);

  let serialNo = 1;

  const getVAlignMargin = (rowSpan) => {
    if (rowSpan <= 1) return [0, 2, 0, 2];
    
    const rowHeight = 11.8;           // average height per row (tested)
    const textHeight = 8.5;           // fontSize 8
    const totalHeight = rowSpan * rowHeight;
    const topMargin = Math.max(0, Math.floor((totalHeight - textHeight) / 2) - 1);
    
    return [0, topMargin, 0, 0];
  };

  freightGroups.forEach((group) => {
    const groupSize = group.length;
    const freight = group[0].freight || {};
    const vMargin = getVAlignMargin(groupSize);

    group.forEach((pkg, pkgIdx) => {
      const wt = parseFloat(pkg.charged_weight) || 0;
      const rateRaw = String(pkg.rate ?? "").trim();
      const isNumeric = !isNaN(rateRaw) && !isNaN(parseFloat(rateRaw));
      const rateDisplay = isNumeric
        ? `${parseFloat(rateRaw).toFixed(2)}`
        : rateRaw
          ? rateRaw.charAt(0).toUpperCase() + rateRaw.slice(1)
          : "-";
      const amt = parseFloat(pkg.amount) || 0;

      if (pkgIdx === 0) {
        // First row of group → Apply rowSpan + vertical centering
        loadTableBody.push([
          {
            text: serialNo.toString(),
            fontSize: 8,
            alignment: "center",
            rowSpan: groupSize,
            margin: vMargin,
            noWrap: false,
          },
          {
            text: freight.lr_number || "-",
            fontSize: 8,
            alignment: "center",
            rowSpan: groupSize,
            margin: vMargin,
            noWrap: false,
          },
          {
            text: formatDateLong(freight.loading_date || freight.created_at),
            fontSize: 7,
            alignment: "center",
            rowSpan: groupSize,
            margin: vMargin,
            noWrap: false,
          },
          {
            text: freight.truck_no || truckNo || "-",
            fontSize: 8,
            alignment: "center",
            rowSpan: groupSize,
            margin: vMargin,
            noWrap: false,
          },
          {
            text: freight.from_location || fromLocation || "-",
            fontSize: 8,
            alignment: "center",
            rowSpan: groupSize,
            margin: vMargin,
            noWrap: false,
          },
          {
            text: freight.to_location || toLocation || "-",
            fontSize: 8,
            alignment: "center",
            rowSpan: groupSize,
            margin: vMargin,
            noWrap: false,
          },
          {
            text: pkg.packages || freight.classification_of_goods || "-",
            fontSize: 8,
            alignment: "center",
            margin: [1, 2, 1, 2],
            noWrap: false,
          },
          {
            text: wt.toFixed(3),
            fontSize: 8,
            alignment: "center",
            margin: [0, 2, 0, 2],
            noWrap: false,
          },
          {
            text: rateDisplay,
            fontSize: 8,
            alignment: "center",
            margin: [0, 2, 0, 2],
            noWrap: false,
          },
          {
            text: formatCurrency(amt),
            fontSize: 8,
            alignment: "right",
            margin: [0, 2, 2, 2],
            noWrap: false,
          },
        ]);
      } else {
        // Subsequent rows - only material, weight, rate, amount
        loadTableBody.push([
          {}, {}, {}, {}, {}, {},
          {
            text: pkg.packages || freight.classification_of_goods || "-",
            fontSize: 8,
            alignment: "center",
            margin: [1, 2, 1, 2],
            noWrap: false,
          },
          {
            text: wt.toFixed(3),
            fontSize: 8,
            alignment: "center",
            margin: [0, 2, 0, 2],
            noWrap: false,
          },
          {
            text: rateDisplay,
            fontSize: 8,
            alignment: "center",
            margin: [0, 2, 0, 2],
            noWrap: false,
          },
          {
            text: formatCurrency(amt),
            fontSize: 8,
            alignment: "right",
            margin: [0, 2, 2, 2],
            noWrap: false,
          },
        ]);
      }
    });

    serialNo++;
  });
} 
else {
  loadTableBody.push([
    {
      text: "1",
      fontSize: 8,
      alignment: "center",
      margin: [0, 2, 0, 2],
      noWrap: false,
    },
    {
      text: firstFreight.lr_number || "-",
      fontSize: 8,
      alignment: "center",
      margin: [0, 2, 0, 2],
      noWrap: false,
    },
    {
      text: formatDateLong(
        firstFreight.loading_date || firstFreight.created_at,
      ),
      fontSize: 7,
      alignment: "center",
      margin: [0, 2, 0, 2],
      noWrap: false,
    },
    {
      text: truckNo,
      fontSize: 8,
      alignment: "center",
      margin: [0, 2, 0, 2],
      noWrap: false,
    },
    {
      text: fromLocation,
      fontSize: 8,
      alignment: "center",
      margin: [0, 2, 0, 2],
      noWrap: false,
    },
    {
      text: toLocation,
      fontSize: 8,
      alignment: "center",
      margin: [0, 2, 0, 2],
      noWrap: false,
    },
    {
      text: firstFreight.classification_of_goods || "-",
      fontSize: 8,
      alignment: "center",
      margin: [1, 2, 1, 2],
      noWrap: false,
    },
    {
      text: totalWeight.toFixed(3),
      fontSize: 8,
      alignment: "center",
      margin: [0, 2, 0, 2],
      noWrap: false,
    },
    {
      text: firstFreight.freight_rate
        ? parseFloat(firstFreight.freight_rate).toFixed(2)
        : "-",
      fontSize: 8,
      alignment: "center",
      margin: [0, 2, 0, 2],
      noWrap: false,
    },
    {
      text: formatCurrency(totalFreightAmount),
      fontSize: 8,
      alignment: "right",
      margin: [0, 2, 2, 2],
      noWrap: false,
    },
  ]);
}

  // LR SUBTOTAL
  const freightAmount = parseFloat(bill.freight_amount) || totalFreightAmount;

  loadTableBody.push([
    {
      text: "LR SUBTOTAL",
      bold: true,
      fontSize: 9,
      alignment: "right",
      colSpan: 9,
      fillColor: HEADER_BG,
      margin: [0, 2, 3, 2],
      noWrap: false,
    },
    {},
    {},
    {},
    {},
    {},
    {},
    {},
    {},
    {
      text: formatCurrency(freightAmount),
      bold: true,
      fontSize: 9,
      alignment: "right",
      fillColor: HEADER_BG,
      margin: [0, 2, 2, 2],
      noWrap: false,
    },
  ]);

  // HALTING CHARGE
  const haltingCharge = parseFloat(bill.halting_charge) || 0;
  const haltingMessage = bill.halting_message || "";

  if (haltingCharge > 0 || haltingMessage) {
    const haltingLabel = haltingMessage ? haltingMessage : "HALTING CHARGE";

    loadTableBody.push([
      {
        text: haltingLabel,
        bold: true,
        fontSize: 8,
        alignment: "right",
        colSpan: 9,
        fillColor: "#fff",
        margin: [0, 2, 3, 2],
        noWrap: false,
      },
      {},
      {},
      {},
      {},
      {},
      {},
      {},
      {},
      {
        text: formatCurrency(haltingCharge),
        bold: true,
        fontSize: 9,
        alignment: "right",
        fillColor: "#fff",
        margin: [0, 2, 2, 2],
        noWrap: false,
      },
    ]);
  }

  // GRAND TOTAL
  const grandTotal =
    parseFloat(bill.total_amount) || freightAmount + haltingCharge;

  loadTableBody.push([
    {
      text: "GRAND TOTAL",
      bold: true,
      fontSize: 10,
      alignment: "right",
      colSpan: 9,
      fillColor: "#d9d9d9",
      margin: [0, 3, 3, 3],
      noWrap: false,
    },
    {},
    {},
    {},
    {},
    {},
    {},
    {},
    {},
    {
      text: formatCurrency(grandTotal),
      bold: true,
      fontSize: 10,
      alignment: "right",
      fillColor: "#d9d9d9",
      margin: [0, 3, 2, 3],
      noWrap: false,
    },
  ]);

  // ── DOCUMENT DEFINITION ──────────────────────────────────
  const docDefinition = {
    pageSize: "A4",
    pageOrientation: "portrait",
    pageMargins: [15, 15, 15, 15],
    defaultStyle: {
      fontSize: 10,
      font: "Roboto",
      color: "#000000",
    },
    images: { logo: logo },
    footer: function (currentPage, pageCount) {
      return {
        text: `This electronic generated pdf does not require any physical signature. Date : ${formatDateTime(bill.created_at)}`,
        fontSize: 8,
        alignment: "left",
        margin: [15, 5, 15, 0],
      };
    },

    content: [
      // ═══════════════════════════════════════════════════════
      // TITLE
      // ═══════════════════════════════════════════════════════
      {
        text: "BILL RECEIPT",
        bold: true,
        fontSize: 14,
        alignment: "center",
        margin: [0, 0, 0, 6],
      },

      // ═══════════════════════════════════════════════════════
      // HEADER SECTION
      // ═══════════════════════════════════════════════════════
      {
        columns: [
          {
            width: 90,
            stack: [
              {
                image: "logo",
                width: 70,
                height: 70,
                margin: [0, 5, 0, 0],
              },
            ],
          },
          {
            width: "*",
            stack: [
              {
                text: "Subject to BHILAI jurisdiction",
                fontSize: 9,
                alignment: "right",
                margin: [0, 0, 0, 2],
              },
              {
                text: ownerCompanyName,
                bold: true,
                fontSize: 18,
                alignment: "right",
                color: "#c00000",
                margin: [0, 0, 0, 2],
              },
              {
                text: "(FLEET OWNER & TRANSPORTING CONTRACTORS)",
                bold: true,
                fontSize: 10,
                alignment: "right",
                margin: [0, 0, 0, 2],
              },
              {
                text: "52, R.S.S. Market, Power House, Bhilai - 490011 (C.G.)",
                fontSize: 9,
                alignment: "right",
                margin: [0, 0, 0, 4],
              },
              {
                text: [
                  { text: "MOB", bold: true, fontSize: 9 },
                  {
                    text: " : 7974125386, 7999552398, 9425234581",
                    fontSize: 9,
                  },
                ],
                alignment: "right",
                margin: [0, 0, 0, 0],
              },
            ],
          },
        ],
        margin: [0, 0, 0, 10],
      },

      // ═══════════════════════════════════════════════════════
      // GSTIN & DAILY SERVICE LINE
      // ═══════════════════════════════════════════════════════
      {
        stack: [
          {
            text: [
              { text: "GSTIN NO. : ", bold: true, fontSize: 9 },
              { text: "27AAICV3639B12C", fontSize: 9 },
            ],
          },
          {
            text: [
              { text: "DAILY SERVICE : ", bold: true, fontSize: 9 },
              { text: "ALL OVER INDIA", fontSize: 9 },
            ],
          },
        ],
        margin: [0, 4, 0, 4],
      },

      // Horizontal line
      {
        canvas: [
          {
            type: "line",
            x1: 0,
            y1: 0,
            x2: 565,
            y2: 0,
            lineWidth: 1,
            lineColor: "#000000",
          },
        ],
        margin: [0, 0, 0, 6],
      },

      // ═══════════════════════════════════════════════════════
      // MEMO NO AND DATE ROW
      // ═══════════════════════════════════════════════════════
      {
        columns: [
          {
            width: "*",
            text: [
              { text: "Bill Receipt No. : ", fontSize: 11 },
              {
                text: `PRL/${memoNo}/${getInitials(bill.party_name)}`,
                bold: true,
                fontSize: 12,
              },
            ],
          },
          {
            width: "auto",
            text: [
              { text: "Date : ", bold: true, fontSize: 11 },
              { text: billDate, bold: true, fontSize: 11 },
            ],
            alignment: "right",
          },
        ],
        margin: [0, 0, 0, 3],
      },

      // ═══════════════════════════════════════════════════════
      // COMPANY / PARTY DETAILS LINES
      // ═══════════════════════════════════════════════════════
      {
        columns: [
          { width: 130, text: "M/S Company Name :", fontSize: 10 },
          {
            width: "*",
            text: bill.party_name || "-",
            bold: true,
            fontSize: 11,
          },
        ],
        margin: [0, 2, 0, 2],
      },
      {
        columns: [
          { width: 130, text: "Company Contact No. :", fontSize: 10 },
          { width: "*", text: companyContact, fontSize: 10 },
        ],
        margin: [0, 0, 0, 2],
      },
      {
        columns: [
          { width: 130, text: "GSTIN :", fontSize: 10 },
          { width: "*", text: "27AAICV3639B12C", fontSize: 10 },
        ],
        margin: [0, 0, 0, 2],
      },
      {
        columns: [
          { width: 130, text: "PAN :", fontSize: 10 },
          { width: "*", text: "AAICV3639B", fontSize: 10 },
        ],
        margin: [0, 0, 0, 2],
      },
      {
        columns: [
          { width: 130, text: "Address :", fontSize: 10 },
          { width: "*", text: companyAddress, fontSize: 10 },
        ],
        margin: [0, 0, 0, 2],
      },
      // ═══════════════════════════════════════════════════════
      // BILL RECEIPT TABLE
      // ═══════════════════════════════════════════════════════
      {
        table: {
          headerRows: 2,
          widths: [20, 45, 50, 55, "*", "*", "*", 40, 40, 55], // ← slightly reduced fixed widths
          body: loadTableBody,
        },
        layout: {
          hLineWidth: () => 1,
          vLineWidth: () => 1,
          hLineColor: () => "#000000",
          vLineColor: () => "#000000",
          paddingLeft: () => 2, // ← reduced from 3
          paddingRight: () => 2, // ← reduced from 3
          paddingTop: () => 1, // ← reduced from 2
          paddingBottom: () => 1, // ← reduced from 2
        },
        margin: [0, 8, 0, 8],
      },

      // ═══════════════════════════════════════════════════════
      // ACCOUNT DETAILS - Grey Header + Table
      // ═══════════════════════════════════════════════════════
      {
        table: {
          widths: ["*"],
          body: [
            [
              {
                text: "ACCOUNT DETAILS",
                bold: true,
                fontSize: 11,
                fillColor: HEADER_BG,
                margin: [5, 2, 5, 2],
                border: [false, false, false, false],
              },
            ],
          ],
        },
        layout: {
          hLineWidth: () => 1,
          vLineWidth: () => 1,
          hLineColor: () => "#000000",
          vLineColor: () => "#000000",
        },
      },
      {
        table: {
          widths: ["*", "*", "*", "*", "*"],
          body: [
            [
              {
                text: "ACCOUNT NUMBER",
                bold: true,
                fontSize: 9,
                alignment: "center",
                margin: [2, 5, 2, 5],
              },
              {
                text: "ACCOUNT NAME",
                bold: true,
                fontSize: 9,
                alignment: "center",
                margin: [2, 5, 2, 5],
              },
              {
                text: "IFSC CODE",
                bold: true,
                fontSize: 9,
                alignment: "center",
                margin: [2, 5, 2, 5],
              },
              {
                text: "BANK NAME",
                bold: true,
                fontSize: 9,
                alignment: "center",
                margin: [2, 5, 2, 5],
              },
              {
                text: "PAN CARD NUMBER",
                bold: true,
                fontSize: 9,
                alignment: "center",
                margin: [2, 5, 2, 5],
              },
            ],
            [
              {
                text: "074015080000001",
                fontSize: 9,
                alignment: "center",
                margin: [2, 6, 2, 6],
              },
              {
                text: "Paras Roadlines",
                fontSize: 9,
                alignment: "center",
                margin: [2, 4, 2, 4],
              },
              {
                text: "UBIN0807401",
                fontSize: 9,
                alignment: "center",
                margin: [2, 6, 2, 6],
              },
              {
                text: "Union Bank Of India",
                fontSize: 9,
                alignment: "center",
                margin: [2, 6, 2, 6],
              },
              {
                text: panCardNumber,
                fontSize: 9,
                alignment: "center",
                margin: [2, 6, 2, 6],
              },
            ],
          ],
        },
        layout: {
          hLineWidth: () => 1,
          vLineWidth: () => 1,
          hLineColor: () => "#000000",
          vLineColor: () => "#000000",
          paddingLeft: () => 0,
          paddingRight: () => 0,
          paddingTop: () => 0,
          paddingBottom: () => 0,
        },
        margin: [0, 4, 0, 6],
      },

      // ═══════════════════════════════════════════════════════
      // TERMS & CONDITIONS - Grey Header ONLY
      // ═══════════════════════════════════════════════════════
      {
        table: {
          widths: ["*"],
          body: [
            [
              {
                text: "TERMS & CONDITIONS",
                bold: true,
                fontSize: 11,
                fillColor: HEADER_BG,
                margin: [5, 4, 5, 4],
                border: [false, false, false, false],
              },
            ],
          ],
        },
        layout: {
          hLineWidth: () => 0,
          vLineWidth: () => 0,
        },
        margin: [0, 0, 0, 2],
      },

      // Terms + Signature (NO borders)
      {
        columns: [
          {
            width: "60%",
            stack: [
              {
                text: "  * GST payable by consignor/consignee.",
                fontSize: 9,
                margin: [0, 0, 0, 4],
              },
              {
                text: [
                  { text: "REMARKS : ", bold: true, fontSize: 10 },
                  { text: remarks || "-", fontSize: 10 },
                ],
                margin: [0, 0, 0, 0],
              },
            ],
          },
          {
            width: "40%",
            stack: [
              {
                text: `For,  ${ownerCompanyName}`,
                fontSize: 9,
                bold: true,
                alignment: "right",
                margin: [0, 0, 5, 20],
              },
              {
                text: "AUTHORIZE SIGNATURE",
                bold: true,
                fontSize: 11,
                alignment: "right",
                margin: [0, 15, 5, 0],
              },
            ],
          },
        ],
        margin: [0, 0, 0, 6],
      },
    ],
  };

  return docDefinition;
};

// ──────────────────────────────────────────────────────────────────────────
// MULTI-BILL WRAPPER
// ──────────────────────────────────────────────────────────────────────────
export const generateBillPDF = (billData) => {
  if (!Array.isArray(billData)) billData = [billData];

  const combinedContent = [];
  billData.forEach((bill, idx) => {
    const singleDoc = generateSingleBillPDF(bill);
    if (idx > 0) {
      combinedContent.push({ text: "", pageBreak: "before" });
    }
    combinedContent.push(...singleDoc.content);
  });

  // Use the last bill's date for footer, or current date
  const footerBill = billData[billData.length - 1] || {};

  return {
    pageSize: "A4",
    pageOrientation: "portrait",
    pageMargins: [15, 15, 15, 30], // ← bottom margin for footer
    defaultStyle: { fontSize: 10, font: "Roboto", color: "#000000" },
    images: { logo: logo },
    footer: function (currentPage, pageCount) {
      return {
        text: `This electronic generated pdf does not require any physical signature. Date : ${formatDateTime(footerBill.created_at)}`,
        fontSize: 8,
        alignment: "left",
        margin: [15, 5, 15, 0],
      };
    },
    content: combinedContent,
  };
};

// ──────────────────────────────────────────────────────────────────────────
// EXPORT FUNCTIONS
// ──────────────────────────────────────────────────────────────────────────
export const printSingleBill = (bill) => {
  if (!bill) return alert("No bill data to print");
  const doc = generateSingleBillPDF(bill);
  pdfMake.createPdf(doc).open();
};

export const downloadSingleBill = (bill) => {
  if (!bill) return alert("No bill data to download");
  const doc = generateSingleBillPDF(bill);
  const filename = `BillReceipt_${bill.billing_id || "Bill"}_${new Date().toISOString().split("T")[0]}.pdf`;
  pdfMake.createPdf(doc).download(filename);
};

export const printBillReport = (billData) => {
  if (!billData || billData.length === 0) return alert("No data to print");
  const doc = generateBillPDF(billData);
  pdfMake.createPdf(doc).open();
};

export const downloadBillReport = (billData) => {
  if (!billData || billData.length === 0) return alert("No data to download");
  const doc = generateBillPDF(billData);
  const filename = `BillReceipt_Bills_${new Date().toISOString().split("T")[0]}.pdf`;
  pdfMake.createPdf(doc).download(filename);
};
